import React, { createContext, useContext, ReactNode, useMemo, useCallback } from 'react';

export type Currency = 'CDF' | 'USD' | 'EUR';

interface CurrencyContextType {
  displayCurrency: Currency;
  setDisplayCurrency: (currency: Currency) => void;
  convertPrice: (priceInCents: number, fromCurrency: string, toCurrency: Currency) => number;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

// Taux de change approximatifs (dans une vraie app, ces taux viendraient d'une API)
const EXCHANGE_RATES = {
  CDF: { USD: 0.0004, EUR: 0.00037, CDF: 1 },
  USD: { CDF: 2500, EUR: 0.92, USD: 1 },
  EUR: { CDF: 2700, USD: 1.09, EUR: 1 }
};

export const CurrencyProvider = ({ children }: { children: ReactNode }) => {
  const [displayCurrency, setDisplayCurrencyState] = React.useState<Currency>(() => {
    const saved = localStorage.getItem('displayCurrency');
    return (saved as Currency) || 'CDF';
  });

  const setDisplayCurrency = useCallback((currency: Currency) => {
    setDisplayCurrencyState(currency);
    localStorage.setItem('displayCurrency', currency);
  }, []);

  const convertPrice = useCallback((priceInCents: number, fromCurrency: string, toCurrency: Currency): number => {
    if (fromCurrency === toCurrency) {
      return priceInCents;
    }

    const fromCurrencyKey = fromCurrency as keyof typeof EXCHANGE_RATES;
    const rate = EXCHANGE_RATES[fromCurrencyKey]?.[toCurrency] || 1;
    
    return Math.round(priceInCents * rate);
  }, []);

  const value = useMemo(() => ({
    displayCurrency,
    setDisplayCurrency,
    convertPrice
  }), [displayCurrency, setDisplayCurrency, convertPrice]);

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};